import { ThemeProvider } from '@mui/material';
import { DEFAULT_CURRENCY } from '@weft/types';
import { muiDefaultTheme } from 'apps/app/src/assets/themes/default-theme';
import { vyTheme } from 'apps/app/src/assets/themes/vy-theme';
import { withGlobalSearchParams } from 'apps/app/src/components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { PLAN_CREATION_REASONS } from 'apps/app/src/contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { PLAN_CREATION_STEPS } from 'apps/app/src/contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { CreatePlanProvider } from 'apps/app/src/contexts/create-plan-context/CreatePlanProvider/CreatePlanProvider';
import { FormErrorNotificationProvider } from 'apps/app/src/contexts/form-error-notification-context/FormErrorNotificationProvider';
import { useMeters } from 'apps/app/src/hooks/requests/use-meters';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import { Helmet } from 'react-helmet';
import { CreatePlanPageHeader } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageHeader';
import { CreatePlanPageStepper } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageStepper/CreatePlanPageStepper';
import { CreatePlanPageStepperNavigator } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageStepperNavigator';
import { NewCreatePlanPageContent } from '../../plans/CreatePlanPage/create-plan-page-components/NewCreatePlanPageContent/NewCreatePlanPageContent';
import { CONTRACT_REVISE_STEPS_CTA_LABELS } from '../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import { FormType } from '../../plans/CreatePlanPage/FormType';
import { withPlanForm } from '../../plans/CreatePlanPage/withPlanForm';
import { SaveReviseCurrentDialog } from './contract-revise-page-components/SaveReviseCurrentDialog';
import { SaveReviseRenewalDialog } from './contract-revise-page-components/SaveReviseRenewalDialog';
import { REVISE_CURRENT_STEPS_CONTENT, REVISE_FOR_RENEWAL_STEPS_CONTENT } from './contract-revise-page-consts/reviseStepsContent';
import { useContractRevisePagePlanCreateReason } from './contract-revise-page-hooks/use-contract-revise-page-plan-create-reason';

const ContractRevisePage: React.FC = () => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { contractId } = useValidatedParams('contractId');
  
  const reviseReason = useContractRevisePagePlanCreateReason(contractId);
  const isReviseForRenewal = (reviseReason === PLAN_CREATION_REASONS.ReviseContractForRenewal);
  const stepsContent = isReviseForRenewal ? REVISE_FOR_RENEWAL_STEPS_CONTENT : REVISE_CURRENT_STEPS_CONTENT;
  
  useMeters();
  
  return (
    <>
      <Helmet>
        <title>Revise Contract</title>
      </Helmet>
      <CreatePlanProvider
        planCreationReason={PLAN_CREATION_REASONS.ReviseCurrentContract}
        defaultPlanCreationStep={PLAN_CREATION_STEPS.pricingAndProducts}
        onFinalStep={handleOpen}
      >
        <FormErrorNotificationProvider>
          <CreatePlanPageHeader />
          <ThemeProvider theme={vyTheme}>
            <CreatePlanPageStepper currency={DEFAULT_CURRENCY} />
          </ThemeProvider>
          <ThemeProvider theme={muiDefaultTheme}>
            <NewCreatePlanPageContent stepsContent={stepsContent}/>
          </ThemeProvider>
          <ThemeProvider theme={vyTheme}>
            <CreatePlanPageStepperNavigator
              stepsLabels={CONTRACT_REVISE_STEPS_CTA_LABELS}
              skipSettingsStep={true}
              formClearable={false}
            />
            {isReviseForRenewal ? (
              <SaveReviseRenewalDialog
                open={isDialogOpen}
                onClose={handleClose}
              />
            ) : ( 
              <SaveReviseCurrentDialog
                open={isDialogOpen}
                onClose={handleClose}
              /> 
            )}
          </ThemeProvider>
        </FormErrorNotificationProvider>
      </CreatePlanProvider>
    </>
  );
};

const ContractRevisePageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(withPlanForm(ContractRevisePage, FormType.ReviseContract)));
export { ContractRevisePageWithSuspenseBoundary as ContractRevisePage };